import {
  APP_PIN_HASH_VERSION,
  APP_PIN_PBKDF2_ITERATIONS,
} from '@/constants/appPinLock';

export type AppPinHashRecord = {
  v: number;
  s: string;
  h: string;
};

function bytesToB64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function b64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

async function derivePinBits(pin: string, salt: Uint8Array): Promise<Uint8Array> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: APP_PIN_PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    256
  );
  return new Uint8Array(bits);
}

function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a[i]! ^ b[i]!;
  }
  return diff === 0;
}

export async function hashAppPin(pin: string): Promise<AppPinHashRecord> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await derivePinBits(pin, salt);
  return {
    v: APP_PIN_HASH_VERSION,
    s: bytesToB64(salt),
    h: bytesToB64(hash),
  };
}

export async function verifyAppPin(pin: string, record: AppPinHashRecord): Promise<boolean> {
  if (record.v !== APP_PIN_HASH_VERSION) return false;
  if (typeof record.s !== 'string' || typeof record.h !== 'string') return false;
  try {
    const salt = b64ToBytes(record.s);
    const expected = b64ToBytes(record.h);
    const actual = await derivePinBits(pin, salt);
    return timingSafeEqual(actual, expected);
  } catch {
    return false;
  }
}

export function parseAppPinHashRecord(raw: unknown): AppPinHashRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== APP_PIN_HASH_VERSION) return null;
  if (typeof o.s !== 'string' || o.s.length === 0) return null;
  if (typeof o.h !== 'string' || o.h.length === 0) return null;
  return { v: APP_PIN_HASH_VERSION, s: o.s, h: o.h };
}
